import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Tooltip,
  Select,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import AddIcon from '@mui/icons-material/Add';

const ROUTE_OPTIONS = [
  'PO',
  'IV',
  'IM',
  'SC',
  'PR',
  'Inhaled',
  'Nebulised',
  'Topical',
  'PO/IV',
  'Other',
];

const FREQUENCY_OPTIONS = [
  'STAT',
  'PRN',
  'OM',
  'ON',
  'BD',
  'TDS',
  'QDS',
  'Weekly',
];

const SCHEDULED_FREQUENCIES = ['OM', 'ON', 'BD', 'TDS', 'QDS', 'Weekly'];
const MANUAL_UNITS = ['minutes', 'hours', 'days', 'weeks'];
const AUTO_UNITS = ['hours', 'days', 'weeks'];

function singularize(unit) {
  if (unit === 'minutes') return 'minute';
  if (unit === 'hours') return 'hour';
  if (unit === 'days') return 'day';
  if (unit === 'weeks') return 'week';
  return unit;
}

function pluralize(value, unit) {
  return Number(value) === 1 ? singularize(unit) : unit;
}

function administrationLabel(admin) {
  const unit = pluralize(admin.offset_value, admin.offset_unit);

  if (admin.type === 'auto') {
    return `Auto administered: last ${admin.offset_value} ${unit}`;
  }

  if (admin.type === 'manual') {
    return `Manual administration: ${admin.offset_value} ${unit} ago`;
  }

  return 'Administration';
}

function sortAdministrations(admins = []) {
  return admins.slice().sort((a, b) => {
    if (a.type === 'auto' && b.type !== 'auto') return -1;
    if (a.type !== 'auto' && b.type === 'auto') return 1;
    return 0;
  });
}

function buildRelativeOffsetFromDateTime(selectedDateTime) {
  const now = dayjs();

  if (!selectedDateTime || !selectedDateTime.isValid()) return null;
  if (selectedDateTime.isAfter(now)) return null;

  const diffMinutes = now.diff(selectedDateTime, 'minute');

  if (diffMinutes < 60) {
    return {
      offset_value: Math.max(1, diffMinutes),
      offset_unit: 'minutes',
    };
  }

  const diffHours = now.diff(selectedDateTime, 'hour');
  if (diffHours < 48) {
    return {
      offset_value: Math.max(1, diffHours),
      offset_unit: 'hours',
    };
  }

  const diffDays = now.diff(selectedDateTime, 'day');
  if (diffDays < 21) {
    return {
      offset_value: Math.max(1, diffDays),
      offset_unit: 'days',
    };
  }

  const diffWeeks = now.diff(selectedDateTime, 'week');
  return {
    offset_value: Math.max(1, diffWeeks),
    offset_unit: 'weeks',
  };
}

export default function MedicationEditor({ data = [], onChange }) {
  const [meds, setMeds] = useState(data);

  const [form, setForm] = useState({
    name: '',
    dose: '',
    route: '',
    frequency: '',
  });

  const [selectedDateTime, setSelectedDateTime] = useState(dayjs());

  const [administrations, setAdministrations] = useState([]);

  const [autoOffsetValue, setAutoOffsetValue] = useState('');
  const [autoOffsetUnit, setAutoOffsetUnit] = useState('days');

  const [manualOffsetValue, setManualOffsetValue] = useState('');
  const [manualOffsetUnit, setManualOffsetUnit] = useState('hours');

  const autoAdminDisabled = !SCHEDULED_FREQUENCIES.includes(form.frequency);
  const hasAutoAdministration = administrations.some(
    (admin) => admin.type === 'auto'
  );

  useEffect(() => {
    setMeds(data || []);
  }, [data]);

  const handleAddAutoAdministration = () => {
    if (autoAdminDisabled) return;

    const value = Number(autoOffsetValue);
    if (!value || value <= 0) return;

    const newAutoAdmin = {
      id: crypto.randomUUID(),
      type: 'auto',
      offset_value: value,
      offset_unit: autoOffsetUnit,
    };

    setAdministrations((prev) => {
      const withoutAuto = prev.filter((admin) => admin.type !== 'auto');
      return [newAutoAdmin, ...withoutAuto];
    });

    setAutoOffsetValue('');
    setAutoOffsetUnit('days');
  };

  const handleAddManualAdministration = () => {
    const value = Number(manualOffsetValue);
    if (!value || value <= 0) return;

    setAdministrations((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'manual',
        offset_value: value,
        offset_unit: manualOffsetUnit,
      },
    ]);

    setManualOffsetValue('');
    setManualOffsetUnit('hours');
  };

  const handleAddAdminFromDateTime = () => {
    const relative = buildRelativeOffsetFromDateTime(selectedDateTime);
    if (!relative) return;

    setAdministrations((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'manual',
        offset_value: relative.offset_value,
        offset_unit: relative.offset_unit,
      },
    ]);
  };

  const handleRemoveAdministration = (idToRemove) => {
    setAdministrations((prev) =>
      prev.filter((admin) => admin.id !== idToRemove)
    );
  };

  const handleAddMed = () => {
    if (
      !form.name.trim() ||
      !form.dose.trim() ||
      !form.route.trim() ||
      !form.frequency.trim()
    ) {
      return;
    }

    const newMed = {
      ...form,
      administrations,
    };

    const updated = [...meds, newMed];
    setMeds(updated);
    onChange && onChange(updated);

    setForm({
      name: '',
      dose: '',
      route: '',
      frequency: '',
    });
    setSelectedDateTime(dayjs());
    setAdministrations([]);
    setAutoOffsetValue('');
    setAutoOffsetUnit('days');
    setManualOffsetValue('');
    setManualOffsetUnit('hours');
  };

  const handleDeleteMed = (indexToRemove) => {
    const updated = meds.filter((_, index) => index !== indexToRemove);
    setMeds(updated);
    onChange && onChange(updated);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #60A5FA',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: '#dbeafe',
            borderBottom: '1px solid #60A5FA',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <MedicationOutlinedIcon />
          <Box>
            <Typography sx={{ fontWeight: 700 }}>Medications</Typography>
            <Typography variant="body2" color="text.secondary">
              Add prescriptions and schedule administration times for the patient
              record.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Stack spacing={3}>
            <Box>
              <Typography sx={{ fontWeight: 700, mb: 0.75 }}>
                Prescription details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Enter the prescription details before adding administration times.
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                    lg: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                }}
              >
                <TextField
                  label="Medication name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  fullWidth
                />

                <TextField
                  label="Dose"
                  value={form.dose}
                  onChange={(e) => setForm({ ...form, dose: e.target.value })}
                  fullWidth
                />

                <TextField
                  select
                  label="Route"
                  value={form.route}
                  onChange={(e) => setForm({ ...form, route: e.target.value })}
                  fullWidth
                >
                  {ROUTE_OPTIONS.map((route) => (
                    <MenuItem key={route} value={route}>
                      {route}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Frequency"
                  value={form.frequency}
                  onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                  fullWidth
                >
                  {FREQUENCY_OPTIONS.map((freq) => (
                    <MenuItem key={freq} value={freq}>
                      {freq}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography sx={{ fontWeight: 700, mb: 0.75 }}>
                Administration times
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Optionally add past administrations for this prescription.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    width: '98%',
                    gap: 2,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      border: '1px solid #d1d5db',
                      borderRadius: 2,
                      overflow: 'hidden',
                      flex: 1,
                      opacity: autoAdminDisabled ? 0.6 : 1,
                      bgcolor: autoAdminDisabled ? '#f8fafc' : 'white',
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.1,
                        bgcolor: autoAdminDisabled ? '#f1f5f9' : '#f8fafc',
                        borderBottom: '1px solid #d1d5db',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Tooltip
                        title={
                          autoAdminDisabled
                            ? 'Auto administration is only available for scheduled medications such as OM, ON, BD, TDS, QDS, or Weekly.'
                            : 'Automatically add prior administrations based on the medication frequency. Only one auto administration rule can be added.'
                        }
                        arrow
                        placement="top"
                      >
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 18,
                            cursor: 'help',
                            color: 'text.secondary',
                          }}
                        />
                      </Tooltip>

                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: autoAdminDisabled ? 'text.disabled' : 'text.primary',
                        }}
                      >
                        Auto administration
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1.25}
                      sx={{ p: 2, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <TextField
                        size="small"
                        type="number"
                        label="Value"
                        sx={{ width: 110 }}
                        disabled={autoAdminDisabled}
                        value={autoOffsetValue}
                        onChange={(e) => setAutoOffsetValue(e.target.value)}
                      />

                      <Select
                        size="small"
                        sx={{ minWidth: 120 }}
                        disabled={autoAdminDisabled}
                        value={autoOffsetUnit}
                        onChange={(e) => setAutoOffsetUnit(e.target.value)}
                      >
                        {AUTO_UNITS.map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit.charAt(0).toUpperCase() + unit.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>

                      <Button
                        onClick={handleAddAutoAdministration}
                        disabled={autoAdminDisabled}
                      >
                        {hasAutoAdministration
                          ? 'Replace auto rule'
                          : 'Add auto rule'}
                      </Button>
                    </Stack>

                    {hasAutoAdministration && !autoAdminDisabled && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ px: 2, pb: 2, display: 'block' }}
                      >
                        Only one auto administration rule can be stored. Adding a
                        new one replaces the existing rule.
                      </Typography>
                    )}
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      border: '1px solid #d1d5db',
                      borderRadius: 2,
                      overflow: 'hidden',
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.1,
                        bgcolor: '#f8fafc',
                        borderBottom: '1px solid #d1d5db',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Tooltip
                        title="Manually add administrations relative to the time of the scenario."
                        arrow
                        placement="top"
                      >
                        <InfoOutlinedIcon
                          sx={{ fontSize: 18, cursor: 'help', color: 'text.secondary' }}
                        />
                      </Tooltip>

                      <Typography sx={{ fontWeight: 700 }}>
                        Manual administration
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1.25}
                      sx={{ p: 2, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <TextField
                        size="small"
                        type="number"
                        label="Value"
                        sx={{ width: 110 }}
                        value={manualOffsetValue}
                        onChange={(e) => setManualOffsetValue(e.target.value)}
                      />

                      <Select
                        size="small"
                        sx={{ minWidth: 120 }}
                        value={manualOffsetUnit}
                        onChange={(e) => setManualOffsetUnit(e.target.value)}
                      >
                        {MANUAL_UNITS.map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit.charAt(0).toUpperCase() + unit.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>

                      <Button onClick={handleAddManualAdministration}>
                        Add administration
                      </Button>
                    </Stack>
                  </Paper>
                </Box>
              </Box>

              <Box sx={{ height: '20px' }} />

              {administrations.length === 0 ? (
                <Alert severity="info">No administration times added yet.</Alert>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    border: '1px solid #d1d5db',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {sortAdministrations(administrations).map((admin) => (
                      <Chip
                        key={admin.id}
                        label={administrationLabel(admin)}
                        onDelete={() => handleRemoveAdministration(admin.id)}
                      />
                    ))}
                  </Box>
                </Paper>
              )}
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={handleAddMed}
                sx={{ textTransform: 'none', minWidth: 200 }}
              >
                Save prescription
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: '1px solid #60A5FA',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: '#dbeafe',
            borderBottom: '1px solid #60A5FA',
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>Saved Prescriptions</Typography>
          <Typography variant="body2" color="text.secondary">
            Review and remove saved medication entries.
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          {meds.length === 0 ? (
            <Typography color="text.secondary">No prescriptions saved yet.</Typography>
          ) : (
            <Stack spacing={1.25}>
              {meds.map((med, idx) => (
                <Paper
                  key={idx}
                  elevation={0}
                  sx={{
                    border: '1px solid #d1d5db',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {med.name}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {med.dose} | {med.route} | {med.frequency}
                      </Typography>

                      <Paper
                        elevation={0}
                        sx={{
                          mt: 1,
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          px: 1,
                          py: 1,
                          bgcolor: '#fafafa',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {sortAdministrations(med.administrations || []).map((admin) => (
                            <Chip
                              key={admin.id}
                              size="small"
                              variant="outlined"
                              label={administrationLabel(admin)}
                            />
                          ))}
                        </Box>
                      </Paper>
                    </Box>

                    <IconButton onClick={() => handleDeleteMed(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
}